import realDataAccountingService from './realDataAccountingService';
import accountingService from './accountingService';

class AutoJournalService {
  // Automatically create journal entries from real transactions
  async createJournalEntriesFromRealData(schoolID, startDate, endDate, reduxData) {
    try {
      // Get all financial data
      const financialData = await realDataAccountingService.getRealDashboardData(schoolID, reduxData);
      
      const journalEntries = [];

      // 1. School Fees Collection Entry
      if (financialData.keyMetrics?.totalRevenue > 0) {
        const schoolFeesRevenue = reduxData.students?.reduce((total, student) => {
          if (student.finance && student.finance.length > 0) {
            const latestFinance = student.finance[student.finance.length - 1];
            if (latestFinance.payment && latestFinance.payment.length > 0) {
              const totalPaid = latestFinance.payment.reduce((sum, payment) => {
                if (payment.des !== "Discount") {
                  return sum + (parseFloat(payment.amountpaid) || 0);
                }
                return sum;
              }, 0);
              return total + totalPaid;
            }
          }
          return total;
        }, 0) || 0;

        if (schoolFeesRevenue > 0) {
          journalEntries.push({
            description: 'School Fees Collection - Auto Generated',
            reference: `SF-AUTO-${new Date().toISOString().split('T')[0]}`,
            date: new Date().toISOString().split('T')[0],
            entries: [
              {
                accountCode: '1000',
                accountName: 'Cash and Cash Equivalents',
                description: 'Cash received from school fees collection',
                debit: schoolFeesRevenue,
                credit: 0
              },
              {
                accountCode: '4000',
                accountName: 'School Fees Revenue',
                description: 'School fees revenue earned',
                debit: 0,
                credit: schoolFeesRevenue
              }
            ],
            schoolID,
            createdBy: 'system',
            userName: 'Auto Journal Service'
          });
        }
      }

      // 2. Canteen Revenue Entry
      const canteenRevenue = reduxData.sales?.reduce((total, sale) => {
        if (sale.paymentStatus === 'paid') {
          return total + (sale.total || 0);
        }
        return total;
      }, 0) || 0;

      if (canteenRevenue > 0) {
        journalEntries.push({
          description: 'Canteen Revenue - Auto Generated',
          reference: `CR-AUTO-${new Date().toISOString().split('T')[0]}`,
          date: new Date().toISOString().split('T')[0],
          entries: [
            {
              accountCode: '1000',
              accountName: 'Cash and Cash Equivalents',
              description: 'Cash received from canteen sales',
              debit: canteenRevenue,
              credit: 0
            },
            {
              accountCode: '4100',
              accountName: 'Canteen Revenue',
              description: 'Canteen revenue earned',
              debit: 0,
              credit: canteenRevenue
            }
          ],
          schoolID,
          createdBy: 'system',
          userName: 'Auto Journal Service'
        });
      }

      // 3. Outstanding School Fees Entry
      const outstandingSchoolFees = reduxData.students?.reduce((total, student) => {
        if (student.finance && student.finance.length > 0) {
          const latestFinance = student.finance[student.finance.length - 1];
          const balance = parseFloat(latestFinance.balance) || 0;
          return total + balance;
        }
        return total;
      }, 0) || 0;

      if (outstandingSchoolFees > 0) {
        journalEntries.push({
          description: 'Outstanding School Fees - Auto Generated',
          reference: `OSF-AUTO-${new Date().toISOString().split('T')[0]}`,
          date: new Date().toISOString().split('T')[0],
          entries: [
            {
              accountCode: '1100',
              accountName: 'Accounts Receivable - School Fees',
              description: 'Outstanding school fees receivable',
              debit: outstandingSchoolFees,
              credit: 0
            },
            {
              accountCode: '4000',
              accountName: 'School Fees Revenue',
              description: 'School fees revenue earned but not collected',
              debit: 0,
              credit: outstandingSchoolFees
            }
          ],
          schoolID,
          createdBy: 'system',
          userName: 'Auto Journal Service'
        });
      }

      // 4. Operating Expenses Entry
      const totalExpenses = reduxData.expenses?.reduce((total, expense) => {
        return total + (parseFloat(expense.amountspent) || 0);
      }, 0) || 0;

      if (totalExpenses > 0) {
        // Categorize expenses
        const adminExpenses = this.categorizeExpenses(reduxData.expenses || [], 'administrative');
        const utilities = this.categorizeExpenses(reduxData.expenses || [], 'utilities');
        const maintenance = this.categorizeExpenses(reduxData.expenses || [], 'maintenance');
        const otherExpenses = totalExpenses - adminExpenses - utilities - maintenance;

        journalEntries.push({
          description: 'Operating Expenses - Auto Generated',
          reference: `OE-AUTO-${new Date().toISOString().split('T')[0]}`,
          date: new Date().toISOString().split('T')[0],
          entries: [
            {
              accountCode: '5100',
              accountName: 'Administrative Expenses',
              description: 'Administrative expenses incurred',
              debit: adminExpenses,
              credit: 0
            },
            {
              accountCode: '5200',
              accountName: 'Utilities',
              description: 'Utilities expenses incurred',
              debit: utilities,
              credit: 0
            },
            {
              accountCode: '5300',
              accountName: 'Maintenance and Repairs',
              description: 'Maintenance expenses incurred',
              debit: maintenance,
              credit: 0
            },
            {
              accountCode: '5600',
              accountName: 'Other Operating Expenses',
              description: 'Other operating expenses incurred',
              debit: otherExpenses,
              credit: 0
            },
            {
              accountCode: '1000',
              accountName: 'Cash and Cash Equivalents',
              description: 'Cash paid for operating expenses',
              debit: 0,
              credit: totalExpenses
            }
          ],
          schoolID,
          createdBy: 'system',
          userName: 'Auto Journal Service'
        });
      }

      // 5. Asset Depreciation Entry
      const depreciationExpense = this.calculateDepreciationExpense(reduxData.assets || []);
      if (depreciationExpense > 0) {
        journalEntries.push({
          description: 'Asset Depreciation - Auto Generated',
          reference: `DEP-AUTO-${new Date().toISOString().split('T')[0]}`,
          date: new Date().toISOString().split('T')[0],
          entries: [
            {
              accountCode: '5500',
              accountName: 'Depreciation Expense',
              description: 'Depreciation expense for the period',
              debit: depreciationExpense,
              credit: 0
            },
            {
              accountCode: '1800',
              accountName: 'Accumulated Depreciation',
              description: 'Accumulated depreciation on fixed assets',
              debit: 0,
              credit: depreciationExpense
            }
          ],
          schoolID,
          createdBy: 'system',
          userName: 'Auto Journal Service'
        });
      }

      // 6. Inventory Entry (if canteen sales exist)
      if (canteenRevenue > 0) {
        const inventoryValue = canteenRevenue * 0.3; // 30% of sales as inventory
        journalEntries.push({
          description: 'Inventory Adjustment - Auto Generated',
          reference: `INV-AUTO-${new Date().toISOString().split('T')[0]}`,
          date: new Date().toISOString().split('T')[0],
          entries: [
            {
              accountCode: '1300',
              accountName: 'Inventory - Canteen Products',
              description: 'Canteen inventory value',
              debit: inventoryValue,
              credit: 0
            },
            {
              accountCode: '1000',
              accountName: 'Cash and Cash Equivalents',
              description: 'Cash invested in inventory',
              debit: 0,
              credit: inventoryValue
            }
          ],
          schoolID,
          createdBy: 'system',
          userName: 'Auto Journal Service'
        });
      }

      // Create all journal entries
      const createdEntries = [];
      for (const entry of journalEntries) {
        try {
          const createdEntry = await accountingService.createJournalEntry(entry);
          createdEntries.push(createdEntry);
        } catch (error) {
          console.error('Error creating auto journal entry:', error);
        }
      }

      return createdEntries;
    } catch (error) {
      console.error('Error creating journal entries from real data:', error);
      throw error;
    }
  }

  // Helper method to categorize expenses
  categorizeExpenses(expenses, keyword) {
    return expenses
      .filter(expense => 
        expense.purpose?.toLowerCase().includes(keyword.toLowerCase()) ||
        expense.description?.toLowerCase().includes(keyword.toLowerCase())
      )
      .reduce((sum, expense) => sum + (parseFloat(expense.amountspent) || 0), 0);
  }

  // Calculate depreciation expense
  calculateDepreciationExpense(assets) {
    const currentYear = new Date().getFullYear();
    return assets.reduce((sum, asset) => {
      const purchaseYear = new Date(asset.purchaseDate).getFullYear();
      const age = currentYear - purchaseYear;
      const depreciationRate = asset.depreciationRate || 0.1; // 10% default
      const annualDepreciation = (asset.purchaseCost || 0) * depreciationRate;
      return sum + (annualDepreciation * age);
    }, 0);
  }

  // Sync real data with accounting system
  async syncRealDataWithAccounting(schoolID, reduxData) {
    try {
      // 1. Ensure chart of accounts exists
      let accounts = await accountingService.getChartOfAccounts(schoolID);
      if (!accounts || accounts.length === 0) {
        accounts = await realDataAccountingService.initializeChartOfAccounts(
          schoolID,
          'system',
          'Auto Sync Service'
        );
      }

      // 2. Create journal entries from real transactions
      const endDate = new Date();
      const startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1); // Current month
      
      const journalEntries = await this.createJournalEntriesFromRealData(
        schoolID,
        startDate,
        endDate,
        reduxData
      );

      return {
        accountsCreated: accounts.length,
        journalEntriesCreated: journalEntries.length,
        syncDate: new Date()
      };
    } catch (error) {
      console.error('Error syncing real data with accounting:', error);
      throw error;
    }
  }

  // Get real-time financial summary
  async getRealTimeFinancialSummary(schoolID, reduxData) {
    try {
      const financialData = await realDataAccountingService.getRealDashboardData(schoolID, reduxData);
      
      return {
        totalRevenue: financialData.keyMetrics?.totalRevenue || 0,
        totalExpenses: financialData.keyMetrics?.totalExpenses || 0,
        netIncome: financialData.keyMetrics?.netIncome || 0,
        cashBalance: financialData.keyMetrics?.cashBalance || 0,
        outstandingFees: this.calculateOutstandingFees(reduxData),
        lastUpdated: new Date()
      };
    } catch (error) {
      console.error('Error getting real-time financial summary:', error);
      throw error;
    }
  }

  // Calculate outstanding fees
  calculateOutstandingFees(reduxData) {
    const schoolFeesOutstanding = reduxData.students?.reduce((total, student) => {
      if (student.finance && student.finance.length > 0) {
        const latestFinance = student.finance[student.finance.length - 1];
        return total + (parseFloat(latestFinance.balance) || 0);
      }
      return total;
    }, 0) || 0;

    return {
      schoolFees: schoolFeesOutstanding,
      canteenFees: 0, // Canteen fees are typically paid immediately
      total: schoolFeesOutstanding
    };
  }
}

export default new AutoJournalService();
